import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import Theme from 'core/themes/model'
import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import FontAwesomeIcon from '../FontAwesomeIcon'

interface AccordionProps {
  id: string
  title: string | React.ReactNode
  className?: string
  icon?: string
  iconRotate?: number
  // Tracker to help with accordion in accordion, parent's height
  // is not updated properly upon opening/closing a child accordion
  // so we need to handle it
  hasNestedAccordion?: any

  // Controlled accordion
  open?: boolean
  onClick?: () => void
}
export default function Accordion({
  id,
  title,
  children,
  open = undefined,
  className,
  onClick,
  icon = 'chevron-right',
  iconRotate = 90,
  hasNestedAccordion,
  ...props
}: PropsWithChildren<AccordionProps>) {
  const [active, toggleActive] = useState(open || false)
  const [height, setHeight] = useState(0)
  const isControlledComponent = open !== undefined && onClick !== undefined

  const content = useRef<HTMLDivElement>(null)
  const classes = useStyles({ active, open, height, iconRotate, hasNestedAccordion })

  const titleComponent = useMemo(
    () => (typeof title === 'string' ? <p className={classes.accordionTitle}>{title}</p> : title),
    [title],
  )

  useEffect(() => {
    if (isControlledComponent) {
      toggleActive(open)
    }
  }, [open, isControlledComponent])

  useEffect(() => {
    if (content.current) {
      const contentHeight = content.current.scrollHeight
      // TODO: Fix this issue for accordion within accordion
      // Set some arbitrary high number to handle accordion in accordion
      // note this will not work if the contents are even larger than this
      setHeight(active ? (hasNestedAccordion ? 2000 : contentHeight) : 0)
    }
  }, [active, children, hasNestedAccordion])

  const handleToggleClick = () => {
    isControlledComponent ? onClick() : toggleActive(!active)
  }

  return (
    <div className={clsx(classes.accordionContainer, className)} id={id}>
      <div className={clsx(classes.accordionTopBar, 'accordionTopBar')} onClick={handleToggleClick}>
        {titleComponent}
        <FontAwesomeIcon solid size="xs" className={clsx(classes.icon, 'toggleIcon')}>
          {icon}
        </FontAwesomeIcon>
      </div>

      <div
        ref={content}
        className={clsx(classes.accordionContent, 'accordionContent')}
        style={{ maxHeight: `${height}px` }}
        {...props}
      >
        {children}
      </div>
    </div>
  )
}
interface AcccordionStylProps {
  active: boolean
  height: number
  open: boolean
  iconRotate: number
  hasNestedAccordion: boolean
}
const useStyles = makeStyles<Theme, AcccordionStylProps>((theme: Theme) => ({
  accordionContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 400,
    border: `1px solid ${theme.components.accordion.border}`,
    borderRadius: 4,
  },
  accordionTopBar: {
    backgroundColor: ({ active, hasNestedAccordion }) =>
      hasNestedAccordion
        ? theme.palette.grey['000']
        : active
        ? `${theme.components.accordion.activeBackground}`
        : `${theme.components.accordion.background}`,
    color: `${theme?.components?.typography?.default}`,
    cursor: 'pointer',
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'background-color 0.6s ease',
    borderRadius: 4,
  },

  accordionTitle: {
    fontWeight: 600,
    fontSize: 14,
    textAlign: 'left',
  },
  accordionContent: {
    overflow: ({ active }) => (active ? 'visible' : 'hidden'),
    transition: 'max-height 0.6s ease',
    maxHeight: 0,
  },
  icon: {
    transition: 'transform 0.6s ease',
    transform: ({ active, iconRotate }) => (active ? `rotate(${iconRotate}deg)` : 'rotate(0deg)'),
    fontSize: 16,
    padding: 10,
  },
}))
